import React, { Component } from 'react';
import { SnowSkiMapContainer } from './snowSkiMapContainer';
import {
    Map,
    GeoJsonLayer
} from '@weatherbug/react';
import '../../../../node_modules/@weatherbug/react/dist/styles.css';
import '../../../../node_modules/mapbox-gl/dist/mapbox-gl.css';
import PropTypes from 'prop-types';
import { reduxConnect } from '../../common';
import mapboxgl from 'mapbox-gl';
import { SnowSkiPopup } from './snowSkiPopup';
import ReactDOMServer from 'react-dom/server';
import { getQueryParameterByName, updateQueryStringParameter } from '../../common/utilities/queryParameters';
import { ngDeps } from '../../common/utilities/ngDependencies';
import { BotMap, BotMapView } from '../common/botMapView/botMapView';
import { translate } from '../../common/translation/translate';

const defaultZoom = 6.2;

const mapImages = {
    resortIcon: '/images/map-pin-snow-ski.png'
};

const mapIconStyle = {
    'text-color': 'white'
};

const mapIconLayout = {
    'icon-image': 'resortIcon',
    'icon-size': .5,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'text-offset': [1, -1.4],
    'text-field': '{index}',
    'text-size': 12,
    'text-font': ['Open Sans Bold']
};

const mapLayers = [
    {
        id: 'snowSkiLayer',
        layout: mapIconLayout,
        type: 'symbol',
        paint: mapIconStyle
    }
];

const mapStoreStateToProps = storeState => {
    const appState = storeState?.appState?.appState || {};
    const { mapboxAccessToken, authId, secret, stdParameters, user } = appState || {};
      
    const locality = user?.ActiveLocation?.Locality;

    const { IsBot } = user || {};

    return {
        latitude: locality?.Latitude,
        longitude: locality?.Longitude,
        mapboxAccessToken,
        authId,
        secret,
        stdParameters,
        isBot: IsBot
    };
};

const mapDispatchToProps = {};

@reduxConnect(mapStoreStateToProps, mapDispatchToProps)
export class SnowSkiMap extends Component {
    static displayName = 'SnowSkiMap';

    static propTypes = {
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        mapboxAccessToken: PropTypes.string,
        isBot: PropTypes.bool
    };

    constructor(props) {
        super(props);

        const center = getQueryParameterByName('center');
        let initialLatitude = props.latitude;
        let initialLongitude = props.longitude;
        let initialZoom = defaultZoom;

        if (center) {
            const centerArr = center.split(',');
            initialLatitude = Number(centerArr[0]);
            initialLongitude = Number(centerArr[1]);
            initialZoom = Number(centerArr[2]);
        } else {
            this.setCenterUrlParam(initialLatitude, initialLongitude, initialZoom);
        }

        this.state = {
            resortGeoJson: {
                type: 'FeatureCollection',
                features: []
            },
            initialLatitude,
            initialLongitude,
            initialZoom,
            latitude: props.latitude,
            longitude: props.longitude
        };

        this.featureSelected = this.featureSelected.bind(this);
        this.onPanned = this.onPanned.bind(this);
        this.onZoomChanged = this.onZoomChanged.bind(this);
        this.tryLoadData = this.tryLoadData.bind(this);
    }

    setCenterUrlParam(newLatitude, newLongitude, newZoom) {
        const center = getQueryParameterByName('center');
        let latitude = newLatitude;
        let longitude = newLongitude;
        let zoom = newZoom;

        if (center) {
            const centerArr = center.split(',');
            latitude = latitude || centerArr[0];
            longitude = longitude || centerArr[1];
            zoom = zoom || centerArr[2];
        } else {
            latitude = latitude || this.props.latitude;
            longitude = longitude || this.props.longitude;
            zoom = zoom || defaultZoom;
        }

        if(latitude && longitude && zoom) {
            const newCenter = `${latitude},${longitude},${zoom}`;
            const newUrl = updateQueryStringParameter(window.location.href, 'center', newCenter);
            window.history.replaceState('', '', newUrl);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const { latitude, longitude } = props;
        if (state.latitude !== latitude
           || state.longitude !== longitude) {
            return {
                initialLatitude: latitude,
                initialLongitude: longitude,
                latitude: latitude,
                longitude: longitude
            };
        }

        return null;
    }

    componentDidMount() {
        if(this.props.latitude && this.props.longitude) {
            this.tryLoadData(true);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { initialLatitude, initialLongitude } = this.state;
        const { latitude, longitude, authId } = this.props;

        if (prevProps.initialLatitude !== initialLatitude
           || prevState.initialLongitude !== initialLongitude) {
            this.setCenterUrlParam(initialLatitude, initialLongitude);
        }

        if (latitude && prevProps.latitude !== latitude
            || longitude && prevProps.longitude !== longitude) {
            const resortGeoJson = {
                type: 'FeatureCollection',
                features: []
            };
            this.setState(resortGeoJson);
            this.tryLoadData();
        }
    }

    async tryLoadData(initialLoad) {
        const { latitude, longitude } = this.props;
        if (initialLoad || latitude && latitude !== this.currentDataLatitude || longitude && longitude !== this.currentDataLongitude) {
            this.currentDataLatitude = latitude;
            this.currentDataLongitude = longitude;

            const result = await ngDeps.snowskiDataManager.GetSnowskiData(latitude, longitude);
            const resortGeoJson = {
                type: 'FeatureCollection',
                features: []
            };

            if (result && result.r) {
                const resortIndex = getQueryParameterByName('index');
                if (resortIndex !== null && resortIndex >= 0) {
                    resortGeoJson.features.push(this.mapResortDataToGeoJsonFeature(result.r[resortIndex], Number(resortIndex), true));
                } else {
                    resortGeoJson.features = result.r.map((resortData, index) => {
                        return this.mapResortDataToGeoJsonFeature(resortData, index);
                    });
                }
            }
            this.setState({
                resortGeoJson
            });
        }
    }

    mapResortDataToGeoJsonFeature(resortData, index, disablePopup) {
        return {
            geometry: {
                type: 'Point',
                coordinates: [
                    resortData.Longitude,
                    resortData.Latitude
                ]
            },
            properties: {
                disablePopup: disablePopup || resortData.disablePopup,
                resortName: resortData.ResortData.resortName,
                street: resortData.ResortData.info.location.street,
                city: resortData.ResortData.info.location.city,
                state: resortData.ResortData.info.location.stateName,
                zip: resortData.ResortData.info.location.zip,
                numLifts: resortData.ResortData.info.lifts.numLifts,
                liftsOpen: resortData.ResortData.snowreport.report.liftsReport.liftsOpen,
                liftsOpenWeekend: resortData.ResortData.snowreport.report.liftsOpenWeekend,
                surfaceTop: resortData.ResortData.snowreport.report.snowQuality.onSlope.surfaceTop,
                index: disablePopup || resortData.disablePopup ? '+' : index + 1
            },
            type: 'Feature'
        };
    }

    onPanned(latitude, longitude) {
        this.setCenterUrlParam(latitude, longitude, undefined);
    }

    onZoomChanged(zoom) {
        this.setCenterUrlParam(undefined, undefined, zoom);
    }

    featureSelected(feature, map) {
        const { resortName, street, city, state, zip, numLifts, liftsOpenWeekend, liftsOpen, surfaceTop, disablePopup } = feature.properties;

        if (!disablePopup) {
            const popupHTML = ReactDOMServer.renderToStaticMarkup(
                <SnowSkiPopup resortName={resortName}
                    street={street}
                    city={city}
                    state={state}
                    zip={zip}
                    numLifts={numLifts}
                    liftsOpenWeekend={liftsOpenWeekend}
                    liftsOpen={liftsOpen}
                    surfaceTop={surfaceTop} />
            );

            const { coordinates } = feature.geometry;
            if (coordinates?.length) {
                new mapboxgl.Popup()
                    .setLngLat(coordinates)
                    .setHTML(popupHTML)
                    .addTo(map);
                
                this.setCenterUrlParam(coordinates[1], coordinates[0], undefined);
            }
        }
    }

    render() {
        const { mapboxAccessToken, latitude, longitude, isBot } = this.props;
        const { resortGeoJson,
            initialLatitude,
            initialLongitude,
            initialZoom } = this.state;

        const centerLatitude = initialLatitude || 0;
        const centerLongitude = initialLongitude || 0;
        const centerMarkerLatitude = latitude || 0;
        const centerMarkerLongitude = longitude || 0;

        return <SnowSkiMapContainer>
            { isBot 
                ? <BotMapView>{translate('snowSkiMap')}</BotMapView>
                : <Map
                    mapboxAccessToken={mapboxAccessToken}
                    initialLatitude={centerLatitude}
                    initialLongitude={centerLongitude}
                    initialZoom={initialZoom}
                    latitude={centerLatitude}
                    longitude={centerLongitude}
                    enableDragRotate={true}
                    enablePan={true}
                    enableZoom={true}
                    showFullscreenControl={false}
                    showNavigationControl={false}
                    showCenterMarker={true}
                    centerMarkerLatitude={centerMarkerLatitude}
                    centerMarkerLongitude={centerMarkerLongitude}
                    onPanned={this.onPanned}
                    onZoomChanged={this.onZoomChanged}
                    smallPinImageUrl='img/map-pin-active.png'
                    bigPinImageUrl='img/map-pin-active.png'
                    extension={(map) => {
                        return resortGeoJson && mapLayers && <GeoJsonLayer map={map} geojson={resortGeoJson}
                            featureIcons={mapImages} layers={mapLayers} featureSelected={this.featureSelected} panOnSelection={true}
                            requirePointId={false}
                        ></GeoJsonLayer>;
                    }}>
                </Map> }
        </SnowSkiMapContainer>;
    }
}
